import { useState } from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { Target, Plus } from 'lucide-react';
import { Shell } from '../components/layout/Shell';
import { useSetsList, useCreateSet, useDeleteSet, useSetTemplates } from '../hooks/useSets';
import { useTranslation } from '../i18n';
import { Route as rootRoute } from './__root';
import styles from './sets.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sets',
  component: SetsPage,
});

function SetsPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useSetsList();
  const { data: templatesData } = useSetTemplates();
  const createSet = useCreateSet();
  const deleteSet = useDeleteSet();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');

  const sets = data?.data ?? [];
  const templates = templatesData?.data ?? [];

  function handleCreate() {
    if (!newName.trim()) return;

    const template = templates.find((t) => t.name === selectedTemplate);
    createSet.mutate(
      {
        name: newName.trim(),
        description: newDesc,
        platform: template?.platform,
        items: template
          ? template.items.map((name) => ({ name, platform: template.platform }))
          : [],
      },
      {
        onSuccess: () => {
          setShowCreate(false);
          setNewName('');
          setNewDesc('');
          setSelectedTemplate('');
        },
      }
    );
  }

  return (
    <Shell>
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>
            <Target size={20} strokeWidth={1.75} />
            {t('sets.title')}
          </h1>
          <button className={styles.addBtn} onClick={() => setShowCreate(!showCreate)}>
            <Plus size={14} strokeWidth={2} />
            {t('sets.createSet')}
          </button>
        </div>

        {/* Create dialog */}
        {showCreate && (
          <div className={styles.createCard}>
            <div className={styles.createTitle}>{t('sets.newSet')}</div>
            <div className={styles.field}>
              <label className={styles.label}>{t('sets.setName')}</label>
              <input
                className={styles.input}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t('sets.setNamePlaceholder')}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>{t('sets.description')}</label>
              <textarea
                className={styles.textarea}
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder={t('sets.descriptionPlaceholder')}
                rows={2}
              />
            </div>
            {templates.length > 0 && (
              <div className={styles.field}>
                <label className={styles.label}>{t('sets.fromTemplate')}</label>
                <select
                  className={styles.select}
                  value={selectedTemplate}
                  onChange={(e) => {
                    setSelectedTemplate(e.target.value);
                    if (e.target.value) {
                      const tmpl = templates.find((t) => t.name === e.target.value);
                      if (tmpl && !newName) setNewName(tmpl.name);
                    }
                  }}
                >
                  <option value="">{t('sets.customSet')}</option>
                  {templates.map((tmpl) => (
                    <option key={tmpl.name} value={tmpl.name}>
                      {tmpl.name} ({tmpl.items.length} items)
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className={styles.createActions}>
              <button className={styles.cancelBtn} onClick={() => setShowCreate(false)}>
                {t('common.cancel')}
              </button>
              <button
                className={styles.submitBtn}
                onClick={handleCreate}
                disabled={!newName.trim() || createSet.isPending}
              >
                {t('sets.create')}
              </button>
            </div>
          </div>
        )}

        {/* Sets grid */}
        {isLoading ? (
          <div className={styles.loading}>{t('common.loading')}</div>
        ) : sets.length === 0 ? (
          <div className={styles.empty}>
            <Target size={48} strokeWidth={1} />
            <div className={styles.emptyTitle}>{t('sets.emptyTitle')}</div>
            <div className={styles.emptyDesc}>{t('sets.emptyDesc')}</div>
          </div>
        ) : (
          <div className={styles.grid}>
            {sets.map((set) => (
              <Link
                key={set.id}
                to="/sets/$setId"
                params={{ setId: set.id }}
                className={styles.setCard}
              >
                <div className={styles.setName}>{set.name}</div>
                {set.platform && <span className={styles.platformBadge}>{set.platform}</span>}
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${set.completionPercent}%` }}
                  />
                </div>
                <div className={styles.setMeta}>
                  {set.ownedItems} / {set.totalItems} ({set.completionPercent}%)
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}
